import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getEmployeeDocumentStatus } from '@/services/documentRequirementsService';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getApiErrorMessage } from '@/lib/apiError';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  FileText, Download, Eye, Search, Filter, Upload, Folder, Trash2,
  Shield, CreditCard, User, Heart, Receipt, Loader2, RefreshCw,
} from 'lucide-react';
import {
  getMyProfile, getDocuments, uploadDocument, deleteDocument,
  getCompanyDocuments, downloadCompanyDocument, type CompanyDoc,
} from '@/lib/hrApi';
import { creditApi } from '@/features/credit/api/creditApi';
import type { CreditAgreement, CreditApplication } from '@/features/credit/types';
import { useC, useDark, type C as ColorSet } from '@/features/shared/theme/kaziColors';
import { useIsMobile } from '@/features/shared/hooks/use-mobile';

interface DocItem {
  id: string | number;
  name: string;
  type: string;
  date: string;
  size: string;
  status: string;
  category: string;
  url?: string;
}

const fmtSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const CATEGORY_ICON: Record<string, React.ComponentType<any>> = {
  Legal: Shield, Payroll: CreditCard, Benefits: User, HR: Heart, Tax: Receipt,
  'Company Policy': Shield, Personal: User,
};

// One of the five buckets shown as cards. Documents don't carry a category from
// the backend, so we derive one (by upload type for personal docs, by filename
// keywords for shared company docs). Default bucket is HR.
const CATEGORY_KEYS = ['Legal', 'Payroll', 'Benefits', 'HR', 'Tax'] as const;
type CategoryKey = (typeof CATEGORY_KEYS)[number];

const TYPE_CATEGORY: Record<string, CategoryKey> = {
  contract: 'Legal', id: 'Legal', passport: 'Legal', driver_license: 'Legal', nda: 'Legal',
  degree: 'HR', certificate: 'HR',
  payslip: 'Payroll', payroll: 'Payroll',
  tax: 'Tax',
};
const categoryForType = (t?: string): CategoryKey => TYPE_CATEGORY[(t || '').toLowerCase()] || 'HR';

const categoryForName = (name?: string): CategoryKey => {
  const n = (name || '').toLowerCase();
  if (/benefit|insurance|pension/.test(n)) return 'Benefits';
  if (/tax|paye|wht|p9/.test(n)) return 'Tax';
  if (/payroll|salary|expense|travel|payslip|allowance/.test(n)) return 'Payroll';
  if (/contract|legal|nda|agreement|terms/.test(n)) return 'Legal';
  return 'HR'; // handbooks, leave policy, code of conduct, etc.
};

// Prefer the document's real, stored category; fall back to the filename guess only for
// legacy company docs uploaded before categories existed.
const companyCategory = (category?: string | null, name?: string): CategoryKey => {
  const c = (category || '').trim();
  return (CATEGORY_KEYS as readonly string[]).includes(c) ? (c as CategoryKey) : categoryForName(name);
};

// ── v5 design primitives ────────────────────────────────────────────────────
function DocumentItem({ name, category, date, size, onDownload, onPreview, onDelete, downloading, C, dark }: {
  name: string; category: string; date: string; size: string;
  onDownload?: () => void; onPreview?: () => void; onDelete?: () => void; downloading?: boolean; C: ColorSet; dark: boolean;
}) {
  const [hov, setHov] = useState(false);
  const Icon = CATEGORY_ICON[category] || FileText;
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, background: hov ? (dark ? C.creamDeep : C.cream) : "transparent", border: `1px solid ${hov ? C.borderMed : "transparent"}`, transition: "all 0.2s" }}>
      <div style={{ width: 44, height: 44, borderRadius: 11, background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={20} color={C.orange} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{ fontSize: 14, fontWeight: 600, color: C.charcoal, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.charcoalLight }}>{category}</span>
          {date ? <><span style={{ fontSize: 11, color: C.charcoalSubtle }}>•</span><span style={{ fontSize: 11, color: C.charcoalSubtle }}>{date}</span></> : null}
          {size && size !== '—' ? <><span style={{ fontSize: 11, color: C.charcoalSubtle }}>•</span><span style={{ fontSize: 11, color: C.charcoalSubtle }}>{size}</span></> : null}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {onPreview ? (
          <button onClick={onPreview} title="Preview" type="button" style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Eye size={14} color={C.charcoalMid} />
          </button>
        ) : null}
        <button onClick={onDownload} disabled={!onDownload || downloading} title="Download" type="button"
          style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: C.orange, display: "flex", alignItems: "center", justifyContent: "center", cursor: onDownload ? "pointer" : "not-allowed", opacity: onDownload ? 1 : 0.5 }}>
          {downloading ? <Loader2 size={14} color="#fff" className="animate-spin" /> : <Download size={14} color="#fff" />}
        </button>
        {onDelete ? (
          <button onClick={onDelete} title="Delete" type="button"
            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Trash2 size={14} color="#dc2626" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CategoryCard({ icon: Icon, label, count, color, bgColor, C, dark, onClick, selected }: {
  icon: React.ComponentType<any>; label: string; count: number; color: string; bgColor: string; C: ColorSet; dark: boolean;
  onClick?: () => void; selected?: boolean;
}) {
  const [hov, setHov] = useState(false);
  const active = hov || selected;
  return (
    <div onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: selected ? bgColor : (dark ? C.creamDeep : C.white), border: `1.5px solid ${selected ? color : (hov ? color + "44" : C.border)}`, borderRadius: 14, padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, transition: "all 0.25s", cursor: "pointer", transform: active ? "translateY(-4px)" : "none", boxShadow: active ? `0 8px 24px ${color}22` : "none" }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: bgColor, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", transform: active ? "scale(1.1)" : "scale(1)" }}>
        <Icon size={26} color={color} />
      </div>
      <div style={{ textAlign: "center" }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: C.charcoal, marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 11, color: C.charcoalLight }}>{count} document{count !== 1 ? "s" : ""}</p>
      </div>
    </div>
  );
}

const panel = (C: ColorSet): React.CSSProperties => ({ background: C.white, border: `1px solid ${C.border}`, borderRadius: 18, overflow: "hidden", transition: "background 0.25s" });

export function EmployeeDocuments() {
  const { t } = useTranslation(['documents']);
  const C = useC();
  const dark = useDark();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empId, setEmpId] = useState<string | null>(null);
  const [companyDocs, setCompanyDocs] = useState<CompanyDoc[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | null>(null);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [reqUploadType, setReqUploadType] = useState<{ type: string; label: string; expirationRequired?: boolean; replacesId?: string } | null>(null);
  const [reqExpiry, setReqExpiry] = useState('');
  const [reqNumber, setReqNumber] = useState('');
  const [loanDocs, setLoanDocs] = useState<{ appId: string; id: string; label: string; file_name: string; size_bytes: number; uploaded_at: string }[]>([]);
  const [agreements, setAgreements] = useState<CreditAgreement[]>([]);
  const confirm = useConfirm();
  // Deleting a document is ADMIN-ONLY (owner decision 2026-09-02) — documents
  // are compliance evidence; the server enforces this too (403 otherwise).
  const { isTenantOwner, isHrManager, isTenantAdmin, isSystemAdmin } = useRoleAccess();
  const canDeleteDocs = Boolean(isTenantOwner || isHrManager || isTenantAdmin || isSystemAdmin);

  const handleDelete = async (docId: string | number, name: string) => {
    if (!empId) return;
    const ok = await confirm({
      title: 'Delete document',
      message: `Delete "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(empId, String(docId));
      setDocuments(mapDocs(await getDocuments(empId)));
      toast.success('Document deleted.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not delete the document.'));
    }
  };

  // US-17 — a failed shared-docs fetch used to be swallowed to [] (identical to
  // "the company has no shared docs"), so an outage silently showed an empty
  // Company Policies section with no way to recover. Track the failure and offer
  // a retry, so error and genuinely-empty are distinguishable.
  const [companyDocsError, setCompanyDocsError] = useState(false);
  const loadCompanyDocs = useCallback(() => {
    setCompanyDocsError(false);
    getCompanyDocuments()
      .then((d) => { setCompanyDocs(d); })
      .catch(() => { setCompanyDocs([]); setCompanyDocsError(true); });
  }, []);
  useEffect(() => { loadCompanyDocs(); }, [loadCompanyDocs]);

  // Loan document trail + signed agreements (credit-service; absent for tenants
  // without the credit module — both calls just come back empty).
  useEffect(() => {
    (async () => {
      try {
        const [apps, ags] = await Promise.all([
          creditApi.myApplications().catch(() => [] as CreditApplication[]),
          creditApi.myAgreements().catch(() => [] as CreditAgreement[]),
        ]);
        setAgreements(ags.filter((g) => g.status === 'signed'));
        const perApp = await Promise.all(apps.map(async (a) => {
          try {
            const res = await creditApi.applicationDocuments(a.id);
            return res.documents.map((d) => ({ appId: a.id, id: d.id, label: d.label, file_name: d.file_name, size_bytes: d.size_bytes, uploaded_at: d.uploaded_at }));
          } catch { return []; }
        }));
        setLoanDocs(perApp.flat());
      } catch { /* credit module absent */ }
    })();
  }, []);

  const openCompanyDoc = async (doc: CompanyDoc) => {
    setDownloadingId(doc.id);
    try {
      const blob = await downloadCompanyDocument(doc.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* ignore */ } finally { setDownloadingId(null); }
  };

  const mapDocs = (docs: any[]): DocItem[] =>
    (Array.isArray(docs) ? docs : []).map((d: any, idx: number) => ({
      id: d.id ?? idx,
      name: d.name || d.file_name || d.title || 'Document',
      type: d.document_type || d.type || 'File',
      date: (d.uploaded_at || d.created_at || d.date || '').slice(0, 10),
      size: typeof d.file_size === 'number' ? fmtSize(d.file_size) : (d.size || '—'),
      status: d.status || 'Available',
      category: d.category || categoryForType(d.document_type || d.type),
      url: d.download_url || d.url || d.file_url,
    }));

  useEffect(() => {
    (async () => {
      try {
        const me = await getMyProfile();
        setEmpId(me.id);
        setDocuments(mapDocs(await getDocuments(me.id)));
        try {
          const st: any = await getEmployeeDocumentStatus(me.id);
          setRequirements(st?.requirements ?? []);
        } catch { /* requirement view optional */ }
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load your documents');
      } finally { setLoading(false); }
    })();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !empId) return;
    setUploading(true); setError(null);
    try {
      await uploadDocument(empId, file, 'other');
      setDocuments(mapDocs(await getDocuments(empId)));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Could not upload document');
    } finally { setUploading(false); }
  };

  const refreshDocs = async () => {
    if (!empId) return;
    setDocuments(mapDocs(await getDocuments(empId)));
    try {
      const st: any = await getEmployeeDocumentStatus(empId);
      setRequirements(st?.requirements ?? []);
    } catch { /* optional */ }
  };

  const handleRequirementUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !empId || !reqUploadType) return;
    setUploading(true); setError(null);
    try {
      await uploadDocument(empId, file, reqUploadType.type, {
        expiresAt: reqExpiry || undefined,
        documentNumber: reqNumber || undefined,
        replacesId: reqUploadType.replacesId,
      });
      setReqUploadType(null); setReqExpiry(''); setReqNumber('');
      await refreshDocs();
      toast.success(t('documents:req_uploaded', { defaultValue: 'Uploaded — HR will review it.' }));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, t('documents:upload_failed', { defaultValue: 'Could not upload the document.' })));
    } finally { setUploading(false); }
  };

  const REQ_STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
    MISSING: { bg: 'rgba(220,38,38,0.12)', fg: '#dc2626', label: t('documents:st_missing', { defaultValue: 'Missing' }) },
    UPLOADED: { bg: 'rgba(59,130,246,0.12)', fg: '#3b82f6', label: t('documents:st_uploaded', { defaultValue: 'Uploaded' }) },
    PENDING_VERIFICATION: { bg: 'rgba(234,88,12,0.12)', fg: '#ea580c', label: t('documents:st_pending', { defaultValue: 'Pending review' }) },
    VERIFIED: { bg: 'rgba(22,163,74,0.12)', fg: '#16a34a', label: t('documents:st_verified', { defaultValue: 'Verified' }) },
    REJECTED: { bg: 'rgba(220,38,38,0.12)', fg: '#dc2626', label: t('documents:st_rejected', { defaultValue: 'Rejected' }) },
    EXPIRING_SOON: { bg: 'rgba(234,88,12,0.12)', fg: '#ea580c', label: t('documents:st_expiring', { defaultValue: 'Expires soon' }) },
    EXPIRED: { bg: 'rgba(220,38,38,0.12)', fg: '#dc2626', label: t('documents:st_expired', { defaultValue: 'Expired' }) },
  };

  const categories = [
    { icon: Shield, label: t('documents:categories.legal', { defaultValue: 'Legal' }), key: 'Legal' as CategoryKey, color: "#8B5CF6", bgColor: "rgba(139, 92, 246, 0.12)" },
    { icon: CreditCard, label: t('documents:categories.payroll', { defaultValue: 'Payroll' }), key: 'Payroll' as CategoryKey, color: C.orange, bgColor: C.orangeLight },
    { icon: User, label: t('documents:categories.benefits', { defaultValue: 'Benefits' }), key: 'Benefits' as CategoryKey, color: "#3B82F6", bgColor: "rgba(59, 130, 246, 0.12)" },
    { icon: Heart, label: t('documents:categories.hr', { defaultValue: 'HR' }), key: 'HR' as CategoryKey, color: "#EC4899", bgColor: "rgba(236, 72, 153, 0.12)" },
    { icon: Receipt, label: t('documents:categories.tax', { defaultValue: 'Tax' }), key: 'Tax' as CategoryKey, color: "#10B981", bgColor: "rgba(16, 185, 129, 0.12)" },
  ];

  // Unified, scope-tagged set: shared company docs + the employee's own docs.
  // Used for category counts, drill-down, and the cross-category search.
  type UDoc = { id: string | number; name: string; category: string; date: string; size: string; type: string; scope: 'shared' | 'mine'; url?: string };
  const sharedDocs: UDoc[] = useMemo(
    () => companyDocs.map((d) => ({
      id: d.id, name: d.name, category: companyCategory(d.category, d.name),
      date: d.created_at ? d.created_at.slice(0, 10) : '', size: '', type: 'Company Policy', scope: 'shared' as const,
    })),
    [companyDocs],
  );
  const myDocs: UDoc[] = useMemo(
    () => documents.map((d) => ({ id: d.id, name: d.name, category: d.category, date: d.date, size: d.size, type: d.type, scope: 'mine' as const, url: d.url })),
    [documents],
  );
  const allDocs = useMemo(() => [...sharedDocs, ...myDocs], [sharedDocs, myDocs]);

  const q = searchQuery.trim().toLowerCase();
  const matches = (d: UDoc) =>
    !q || d.name.toLowerCase().includes(q) || d.category.toLowerCase().includes(q) || (d.type || '').toLowerCase().includes(q);
  const countFor = (key: CategoryKey) => allDocs.filter((d) => d.category === key).length;

  // Render any unified doc row, wiring the right download/preview per scope.
  const renderRow = (d: UDoc) => (
    <DocumentItem
      key={`${d.scope}-${d.id}`} C={C} dark={dark}
      name={d.name} category={d.scope === 'shared' ? 'Company Policy' : d.category}
      date={d.date ? (d.scope === 'shared' ? t('documents:added', { date: d.date }) : d.date) : ''} size={d.size || ''}
      downloading={d.scope === 'shared' && downloadingId === d.id}
      onPreview={d.scope === 'mine' && d.url ? () => window.open(d.url, '_blank', 'noopener') : undefined}
      onDownload={
        d.scope === 'shared'
          ? () => { const c = companyDocs.find((x) => x.id === d.id); if (c) void openCompanyDoc(c); }
          : d.url ? () => window.open(d.url, '_blank', 'noopener') : undefined
      }
      onDelete={d.scope === 'mine' && canDeleteDocs ? () => void handleDelete(d.id, d.name) : undefined}
    />
  );

  const selectedMeta = categories.find((c) => c.key === selectedCategory);
  const SelectedIcon = selectedMeta?.icon || Folder;
  const searchResults = q ? allDocs.filter(matches) : [];
  const categoryResults = selectedCategory ? allDocs.filter((d) => d.category === selectedCategory && matches(d)) : [];
  const myVisible = myDocs.filter(matches);
  const sharedVisible = sharedDocs.filter(matches);

  const emptyState = (msg: string) => (
    <div style={{ padding: "40px 20px", textAlign: "center" }}>
      <FileText size={48} color={C.charcoalSubtle} style={{ margin: "0 auto 12px" }} />
      <p style={{ fontSize: 13, color: C.charcoalLight }}>{msg}</p>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.charcoal, letterSpacing: "-0.03em", marginBottom: 6 }}>{t('documents:title', { defaultValue: 'My Documents' })}</h1>
          <p style={{ fontSize: 14, color: C.charcoalLight }}>{t('documents:subtitle', { defaultValue: 'Access and download your employment documents' })}</p>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 10, border: "none", background: C.orange, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          <Upload size={16} /> {uploading ? t('documents:uploading', { defaultValue: 'Uploading…' }) : t('documents:upload', { defaultValue: 'Upload Document' })}
          <input type="file" style={{ display: 'none' }} onChange={handleUpload} aria-label={t('documents:upload', { defaultValue: 'Upload document' })} disabled={uploading} />
        </label>
      </div>

      {/* Search */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, transition: "background 0.25s" }}>
        <Search size={18} color={C.charcoalLight} />
        <input type="text" placeholder={t('documents:search_placeholder', { defaultValue: 'Search documents...' })} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          style={{ border: "none", outline: "none", background: "transparent", fontSize: 14, color: C.charcoal, width: "100%", fontFamily: "inherit" }} />
        <button type="button" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.charcoalMid, fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
          <Filter size={14} /> {t('documents:filter', { defaultValue: 'Filter' })}
        </button>
      </div>

      {error ? <div style={{ padding: 16, background: C.redLight, border: `1px solid ${C.border}`, borderRadius: 12, color: C.redText, fontSize: 13 }}>{error}</div> : null}

      {/* Categories — click to drill into a category (across shared + your docs) */}
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, marginBottom: 16 }}>{t('documents:document_categories', { defaultValue: 'Document Categories' })}</h2>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 16 }}>
          {categories.map((cat) => (
            <CategoryCard key={cat.key} C={C} dark={dark} icon={cat.icon} label={cat.label} color={cat.color} bgColor={cat.bgColor}
              count={countFor(cat.key)} selected={selectedCategory === cat.key}
              onClick={() => setSelectedCategory((cur) => (cur === cat.key ? null : cat.key))} />
          ))}
        </div>
      </div>

      {loading ? (
        <div style={panel(C)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', color: C.charcoalLight, gap: 8 }}>
            <Loader2 size={20} className="animate-spin" /> <span style={{ fontSize: 13 }}>{t('documents:loading_documents', { defaultValue: 'Loading documents…' })}</span>
          </div>
        </div>
      ) : q ? (
        /* Search spans everything — shared + your docs, all categories. */
        <div style={panel(C)}>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
            <Search size={18} color={C.orange} />
            <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:search_results', { defaultValue: 'Search results' })}</h2>
            <span style={{ fontSize: 12, color: C.charcoalLight, marginLeft: "auto" }}>{t('documents:matches_for', { count: searchResults.length, query: searchQuery.trim() })}</span>
          </div>
          <div style={{ padding: "8px 24px 24px" }}>
            {searchResults.length === 0 ? emptyState(t('documents:no_search_match', { defaultValue: 'No documents match your search.' })) : searchResults.map(renderRow)}
          </div>
        </div>
      ) : selectedCategory ? (
        /* Category drill-down — every doc in this category (shared + yours). */
        <div style={panel(C)}>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
            <SelectedIcon size={18} color={selectedMeta?.color || C.orange} />
            <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:category_documents', { category: selectedMeta?.label })}</h2>
            <button type="button" onClick={() => setSelectedCategory(null)}
              style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: C.orange, background: "transparent", border: "none", cursor: "pointer" }}>
              ✕ {t('documents:clear_filter', { defaultValue: 'Clear filter' })}
            </button>
          </div>
          <div style={{ padding: "8px 24px 24px" }}>
            {categoryResults.length === 0 ? emptyState(t('documents:none_in_category', { category: selectedMeta?.label })) : categoryResults.map(renderRow)}
          </div>
        </div>
      ) : (
        <>
          {/* Company Policies — shared with everyone */}
          {companyDocsError && sharedVisible.length === 0 ? (
            <div style={panel(C)}>
              <div style={{ padding: "24px", textAlign: "center" }}>
                <FileText size={40} color={C.charcoalSubtle} style={{ margin: "0 auto 10px" }} />
                <p style={{ fontSize: 13, color: C.charcoalMid, marginBottom: 12 }}>
                  {t('documents:shared_load_error', { defaultValue: "We couldn't load the company's shared documents just now." })}
                </p>
                <button type="button" onClick={loadCompanyDocs}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.charcoalMid, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  <RefreshCw size={14} /> {t('common:retry', { defaultValue: 'Retry' })}
                </button>
              </div>
            </div>
          ) : sharedVisible.length > 0 ? (
            <div style={panel(C)}>
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <Folder size={18} color={C.orange} />
                <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:company_policies_handbook', { defaultValue: 'Company Policies & Handbook' })}</h2>
                <span style={{ fontSize: 12, color: C.charcoalLight, marginLeft: "auto" }}>{t('documents:shared_for_everyone', { defaultValue: 'Shared HR documents for everyone' })}</span>
              </div>
              <div style={{ padding: "8px 24px 24px" }}>{sharedVisible.map(renderRow)}</div>
            </div>
          ) : null}

          {/* Required documents — requirement-aware checklist (compliance program §11) */}
          {requirements.length > 0 && (
            <div style={{ ...panel(C), padding: 20, marginBottom: 18 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:required_title', { defaultValue: 'Required documents' })}</h2>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2, marginBottom: 12 }}>
                {t('documents:required_sub', { defaultValue: 'What your company requires from you, and where each one stands.' })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {requirements.map((r: any) => {
                  const st = REQ_STATUS_STYLE[r.status] || REQ_STATUS_STYLE.MISSING;
                  const actionable = ['MISSING', 'REJECTED', 'EXPIRING_SOON', 'EXPIRED'].includes(r.status);
                  return (
                    <div key={r.type} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.charcoal }}>{r.label}</div>
                        {r.expiresAt ? <div style={{ fontSize: 11, color: C.charcoalSubtle }}>{t('documents:expires', { defaultValue: 'Expires' })} {r.expiresAt}</div> : null}
                        {r.status === 'REJECTED' && r.rejectionReason ? (
                          <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{r.rejectionReason}</div>
                        ) : null}
                      </div>
                      {/* The status sits in its own element beside the document
                          name, so on its own it announces as a bare word. Name
                          the requirement in the accessible label — "Passport:
                          Missing" is usable out of context, "Missing" is not. */}
                      <span
                        aria-label={`${r.label}: ${st.label}`}
                        style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.fg }}
                      >{st.label}</span>
                      {actionable ? (
                        <button type="button" disabled={uploading}
                          onClick={() => { setReqUploadType({ type: r.type, label: r.label, expirationRequired: r.expirationRequired, replacesId: r.documentId || undefined }); setReqExpiry(''); setReqNumber(''); }}
                          style={{ fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, border: 'none', background: C.orange, color: '#fff', cursor: 'pointer' }}>
                          {r.status === 'MISSING' ? t('documents:upload', { defaultValue: 'Upload' }) : t('documents:replace', { defaultValue: 'Replace' })}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {reqUploadType ? (
                <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: dark ? C.creamDeep : C.cream, border: `1px solid ${C.borderMed}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>
                    {t('documents:uploading_type', { defaultValue: 'Upload: {{label}}', label: reqUploadType.label })}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    <input type="text" value={reqNumber} onChange={(e) => setReqNumber(e.target.value)}
                      placeholder={t('documents:doc_number', { defaultValue: 'Document number (optional)' })}
                      style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: 12 }} />
                    {reqUploadType.expirationRequired ? (
                      <label style={{ fontSize: 12, color: C.charcoalLight, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {t('documents:expiry_date', { defaultValue: 'Expiry date' })}
                        <input type="date" value={reqExpiry} onChange={(e) => setReqExpiry(e.target.value)}
                          style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.charcoal, fontSize: 12 }} />
                      </label>
                    ) : null}
                    <label style={{ fontSize: 12, fontWeight: 600, padding: '8px 14px', borderRadius: 8, background: C.orange, color: '#fff', cursor: (reqUploadType.expirationRequired && !reqExpiry) ? 'not-allowed' : 'pointer', opacity: (reqUploadType.expirationRequired && !reqExpiry) ? 0.5 : 1 }}>
                      {uploading ? t('documents:uploading', { defaultValue: 'Uploading…' }) : t('documents:choose_file', { defaultValue: 'Choose file' })}
                      <input type="file" style={{ display: 'none' }} onChange={handleRequirementUpload}
                        disabled={uploading || (reqUploadType.expirationRequired && !reqExpiry)} />
                    </label>
                    <button type="button" onClick={() => setReqUploadType(null)}
                      style={{ fontSize: 12, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.charcoalMid, cursor: 'pointer' }}>
                      {t('documents:cancel', { defaultValue: 'Cancel' })}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* My Documents — the employee's own private documents only */}
          <div style={panel(C)}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:title', { defaultValue: 'My Documents' })}</h2>
              <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{t('documents:your_private_documents', { defaultValue: 'Your private documents' })} · {t('documents:documents_count', { count: myVisible.length })}</p>
            </div>
            <div style={{ padding: "8px 24px 24px" }}>
              {myVisible.length === 0
                ? emptyState(myDocs.length === 0 ? t('documents:no_documents_yet', { defaultValue: 'No documents have been added to your record yet.' }) : t('documents:no_search_match', { defaultValue: 'No documents match your search.' }))
                : myVisible.map(renderRow)}
            </div>
          </div>

          {/* Loans & signed agreements — the credit document trail: what you
              attached to loan requests, plus every signed agreement (the same
              tamper-evident record your lender holds). */}
          {(loanDocs.length > 0 || agreements.length > 0) ? (
            <div style={panel(C)}>
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}` }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: C.charcoal }}>{t('documents:loans_agreements', { defaultValue: 'Loans & Signed Agreements' })}</h2>
                <p style={{ fontSize: 12, color: C.charcoalLight, marginTop: 2 }}>{t('documents:loans_agreements_note', { defaultValue: 'Documents attached to your loan requests and your signed loan agreements.' })}</p>
              </div>
              <div style={{ padding: "8px 24px 24px" }}>
                {agreements.map((g) => (
                  <DocumentItem key={`agr-${g.id}`} C={C} dark={dark}
                    name={t('documents:signed_loan_agreement', { defaultValue: 'Signed Loan Agreement' })}
                    category="Legal" date={(g.createdAt || '').slice(0, 10)} size=""
                    onDownload={() => { void creditApi.downloadAgreement(g.id).catch(() => setError(t('documents:agreement_download_failed', { defaultValue: 'Could not download the agreement.' }))); }} />
                ))}
                {loanDocs.map((d) => (
                  <DocumentItem key={`loan-${d.id}`} C={C} dark={dark}
                    name={d.label} category="Legal" date={(d.uploaded_at || '').slice(0, 10)} size={fmtSize(d.size_bytes)}
                    onDownload={() => { void creditApi.downloadApplicationDocument(d.appId, d.id, d.file_name).catch(() => setError(t('documents:document_download_failed', { defaultValue: 'Could not download the document.' }))); }} />
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default EmployeeDocuments;
